import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  MultiSelect,
  NativeSelect,
  NumberInput,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useSearchParams } from 'react-router-dom'
import { IconBug, IconDownload, IconExternalLink, IconRefresh } from '@/components/icons'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
  PaginationBar,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { debugDatePreset } from '@/lib/debug-date-presets'
import {
  defaultPermissionsForRole,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { formatDateTimeDot, statusLabel, ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type {
  AIAuditLogRow,
  DebugAiRequestTicketRow,
  DebugAiStuckTicketRow,
  DebugAiTicketRow,
  DebugAutoResolveBlockRow,
  DebugAiQueueBlockRow,
  MyPermissionsResponse,
  ChannelRow,
  ProjectRow,
  WorkspaceUserRow,
} from '@/types/api'
import { SettingsLoadTestTab } from '@/pages/settings/SettingsLoadTestTab'
import { DebugMozgobotReportTab } from '@/pages/debug/DebugMozgobotReportTab'
import { DebugShiftBreaksTab } from '@/pages/debug/DebugShiftBreaksTab'
import { DebugEmailBackfillTab } from '@/pages/debug/DebugEmailBackfillTab'
import { DebugTicketsExportTab } from '@/pages/debug/DebugTicketsExportTab'
import { DebugSlowFirstResponseTab } from '@/pages/debug/DebugSlowFirstResponseTab'
import { DebugPendingHistoryTab } from '@/pages/debug/DebugPendingHistoryTab'
import { FailedDeliveriesPanel } from '@/pages/FailedDeliveriesPage'

const PAGE_SIZE_LS = 'sd2_debug_page_size'
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

const AUTO_RESOLVE_KIND_LABELS: Record<string, string> = {
  closing_phrase: 'Закрывающая фраза',
  resolution_confirm: 'Подтверждение решения',
}

const QUEUE_BLOCK_KIND_SHORT: Record<string, string> = {
  processing_lock: 'Lock',
  enqueued_marker: 'Enqueued',
  last_handled_marker: 'Handled id',
  released_from_ai: 'Released',
  redis_dispatch: 'Redis dispatch',
  redis_pending_requeue: 'Redis requeue',
  legacy_fifo: 'Legacy FIFO',
}

const QUEUE_BLOCK_LEGEND: { kind: string; title: string; description: string }[] = [
  {
    kind: 'processing_lock',
    title: 'Lock (БД)',
    description:
      'ai_processing_locked_at — воркер взял тикет в обработку (подготовка контекста). Обычно снимается сам до запроса к GPU. Зависшая метка блокирует повторную постановку в очередь.',
  },
  {
    kind: 'enqueued_marker',
    title: 'Enqueued (БД)',
    description:
      'ai_processing_enqueued_at — защита от дублей при постановке в Celery (TTL ~20 с). Монитор monitor_ai_tickets не ставит вторую задачу, пока метка жива.',
  },
  {
    kind: 'last_handled_marker',
    title: 'Handled id (БД)',
    description:
      'ai_last_handled_customer_message_id — идемпотентность: какое сообщение клиента уже обработано. Снимать только если нужно принудительно переобработать то же сообщение.',
  },
  {
    kind: 'released_from_ai',
    title: 'Released (БД)',
    description:
      'ai_released_to_operator_at — тикет передан живым операторам. AI снова не возьмёт его, пока метка не снята и тикет не назначен на Мозгобота.',
  },
  {
    kind: 'redis_dispatch',
    title: 'Redis dispatch',
    description:
      'Задача process_single_ticket_with_ai уже отправлена в Celery (очередь ai). Пока ключ есть, повторный enqueue не делается.',
  },
  {
    kind: 'redis_pending_requeue',
    title: 'Redis requeue',
    description:
      'Клиент написал во время текущей обработки — после завершения задачи тикет нужно поставить снова. Снимать только если повтор «застрял».',
  },
  {
    kind: 'legacy_fifo',
    title: 'Legacy FIFO',
    description: 'Старые ключи Redis (fifo/pending_set). При массовой очистке удаляются вместе с остальными.',
  },
]

const QUEUE_BLOCK_ALL_KINDS = QUEUE_BLOCK_LEGEND.map((item) => item.kind)

const QUEUE_BLOCK_KIND_SELECT_DATA = QUEUE_BLOCK_LEGEND.map((item) => ({
  value: item.kind,
  label: QUEUE_BLOCK_KIND_SHORT[item.kind] || item.kind,
}))

const QUEUE_BLOCK_FILTER_LS = 'sd2_debug_queue_block_filters'
const DEFAULT_QUEUE_BLOCK_EXCLUDE = ['released_from_ai']
const DEFAULT_QUEUE_BLOCK_CLEAR = QUEUE_BLOCK_ALL_KINDS.filter((k) => k !== 'released_from_ai')

function readSavedQueueBlockFilters(): { exclude: string[]; include: string[]; clear: string[] } {
  try {
    const raw = localStorage.getItem(QUEUE_BLOCK_FILTER_LS)
    if (raw) {
      const parsed = JSON.parse(raw) as {
        exclude?: string[]
        include?: string[]
        clear?: string[]
      }
      const valid = new Set(QUEUE_BLOCK_ALL_KINDS)
      const pick = (arr: unknown) =>
        Array.isArray(arr) ? arr.filter((k): k is string => typeof k === 'string' && valid.has(k)) : []
      return {
        exclude: pick(parsed.exclude).length ? pick(parsed.exclude) : DEFAULT_QUEUE_BLOCK_EXCLUDE,
        include: pick(parsed.include),
        clear: pick(parsed.clear).length ? pick(parsed.clear) : DEFAULT_QUEUE_BLOCK_CLEAR,
      }
    }
  } catch {
    /* ignore */
  }
  return {
    exclude: DEFAULT_QUEUE_BLOCK_EXCLUDE,
    include: [],
    clear: DEFAULT_QUEUE_BLOCK_CLEAR,
  }
}

function kindsToCsv(kinds: string[]): string | undefined {
  return kinds.length > 0 ? kinds.join(',') : undefined
}

function blocksBodyForClear(clearKinds: string[]): { blocks: string[] } | null {
  if (clearKinds.length === 0) return null
  if (clearKinds.length >= QUEUE_BLOCK_ALL_KINDS.length) return { blocks: ['all'] }
  return { blocks: clearKinds }
}

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'new', label: 'Новые' },
  { value: 'open', label: 'Открытые' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'resolved', label: 'Решено' },
  { value: 'closed', label: 'Закрыто' },
]

type DebugTab =
  | 'ai-list'
  | 'stuck'
  | 'ai-requests'
  | 'auto-resolve'
  | 'queue-blocks'
  | 'slow-first-response'
  | 'pending-history'
  | 'mozgobot-report'
  | 'failed-deliveries'
  | 'telegram-backfill'
  | 'email-backfill'
  | 'tickets-export'
  | 'db'
  | 'loadtest'
  | 'shift-breaks'
  | 'ai-audit'

type TelegramBackfillResult = {
  channel_id: string
  applied: boolean
  batches: number
  updates_seen: number
  updates_processed: number
  updates_skipped_old: number
  updates_without_message: number
  last_update_id: number | null
  errors: Array<{ update_id?: number; error: string }>
  note?: string
}

type TicketWorkspaceRepairItem = {
  id: string
  ticket_number: number
  old_workspace_id: string | null
  new_workspace_id: string
  project_id: string
  project_name: string | null
  channel_name: string | null
  channel_type: string | null
  subject: string
  status: string
  created_at: string | null
}

type TicketWorkspaceRepairList = {
  items: TicketWorkspaceRepairItem[]
  total: number
  page: number
  page_size: number
  pages: number
}

type TelegramIngestedTicketItem = {
  id: string
  ticket_number: number
  project_id: string
  project_name: string | null
  project_slug: string | null
  workspace_id: string | null
  project_workspace_id: string
  subject: string
  status: string
  customer_name: string | null
  customer_email: string | null
  customer_external_id: string | null
  messages_count: number
  actual_messages_count: number
  customer_messages_count: number
  operator_messages_count: number
  created_at: string | null
  updated_at: string | null
  last_message_content: string | null
  last_message_type: string | null
  last_message_sender_name: string | null
  last_message_at: string | null
}

type TelegramIngestedTicketList = {
  items: TelegramIngestedTicketItem[]
  total: number
  page: number
  page_size: number
  pages: number
}

function readSavedPageSize(): number {
  try {
    const n = parseInt(localStorage.getItem(PAGE_SIZE_LS) || '', 10)
    if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return n
  } catch {
    /* ignore */
  }
  return 50
}

function ticketRefParts(t: { id: string; ticket_number: number; project_slug?: string | null }) {
  return ticketPathSegmentFromListItem({
    id: t.id,
    ticket_number: t.ticket_number,
    project_slug: t.project_slug,
  })
}

export function DebugPage() {
  const { user } = useAuth()
  const { openOrSelectTab } = useWorkspaceTabs()
  const [searchParams, setSearchParams] = useSearchParams()

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const effectivePerm = perm ?? defaultPermissionsForRole(user?.role)
  const canDebug = hasPermission(effectivePerm, 'access_debug')
  const canAudit = hasPermission(effectivePerm, 'access_ai_audit_log')
  const canEnter = canDebug || canAudit
  const userRole = (user?.role || '').toLowerCase()
  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin'

  const defaultTab: DebugTab = canDebug ? 'ai-list' : 'ai-audit'

  const allowedTabs = useMemo(() => {
    const t: DebugTab[] = []
    if (canDebug) {
      t.push(
        'ai-list',
        'stuck',
        'ai-requests',
        'auto-resolve',
        'queue-blocks',
        'slow-first-response',
        'pending-history',
        'mozgobot-report',
        'failed-deliveries',
        'telegram-backfill',
        'email-backfill',
        'tickets-export',
        'db',
      )
      if (isOwnerOrAdmin) {
        t.push('loadtest', 'shift-breaks')
      }
    }
    if (canAudit) t.push('ai-audit')
    return t
  }, [canDebug, canAudit, isOwnerOrAdmin])

  const tabFromUrl = (searchParams.get('tab') || '') as DebugTab
  const activeTab: DebugTab = allowedTabs.includes(tabFromUrl) ? tabFromUrl : defaultTab

  useEffect(() => {
    if (!allowedTabs.length) return
    if (!allowedTabs.includes(tabFromUrl)) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.set('tab', defaultTab)
          return p
        },
        { replace: true },
      )
    }
  }, [allowedTabs, tabFromUrl, defaultTab, setSearchParams])

  const setTab = useCallback(
    (v: string | null) => {
      const next = (v || defaultTab) as DebugTab
      if (!allowedTabs.includes(next)) return
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.set('tab', next)
          return p
        },
        { replace: true },
      )
    },
    [allowedTabs, defaultTab, setSearchParams],
  )

  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [users, setUsers] = useState<WorkspaceUserRow[]>([])
  const [channels, setChannels] = useState<ChannelRow[]>([])

  const [hours, setHours] = useState(24)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [projectId, setProjectId] = useState('')
  const [status, setStatus] = useState('')
  const [telegramBackfillChannelId, setTelegramBackfillChannelId] = useState('')
  const [telegramBackfillLimit, setTelegramBackfillLimit] = useState(100)
  const [telegramBackfillMaxBatches, setTelegramBackfillMaxBatches] = useState(10)
  const [telegramBackfillTodayOnly, setTelegramBackfillTodayOnly] = useState(true)
  const [telegramBackfillLoading, setTelegramBackfillLoading] = useState(false)
  const [telegramBackfillResult, setTelegramBackfillResult] = useState<TelegramBackfillResult | null>(null)
  const [ticketWorkspaceRepairBusy, setTicketWorkspaceRepairBusy] = useState(false)
  const [ticketWorkspaceRepairListBusy, setTicketWorkspaceRepairListBusy] = useState(false)
  const [ticketWorkspaceRepairPage, setTicketWorkspaceRepairPage] = useState(1)
  const [ticketWorkspaceRepairList, setTicketWorkspaceRepairList] = useState<TicketWorkspaceRepairList | null>(null)
  const [ticketWorkspaceRepairSingleBusy, setTicketWorkspaceRepairSingleBusy] = useState<string | null>(null)
  const [telegramIngestedBusy, setTelegramIngestedBusy] = useState(false)
  const [telegramIngestedPage, setTelegramIngestedPage] = useState(1)
  const [telegramIngestedSearch, setTelegramIngestedSearch] = useState('')
  const [telegramIngestedList, setTelegramIngestedList] = useState<TelegramIngestedTicketList | null>(null)
  const [ticketWorkspaceRepairResult, setTicketWorkspaceRepairResult] = useState<{
    applied: boolean
    tickets_to_fix: number
    customers_to_fix: number
    tickets_updated: number
    tickets_renumbered_due_to_conflict: number
    customers_updated: number
    ambiguous_customers_skipped: number
  } | null>(null)

  const debugFiltersRef = useRef({ hours, dateFrom, dateTo, projectId, status })
  debugFiltersRef.current = { hours, dateFrom, dateTo, projectId, status }

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(readSavedPageSize)
  const pageRef = useRef(page)
  pageRef.current = page

  const [loadingTab, setLoadingTab] = useState<DebugTab | null>(null)
  const loading = loadingTab === activeTab
  const [error, setError] = useState<string | null>(null)

  const [aiTickets, setAiTickets] = useState<DebugAiTicketRow[]>([])
  const [aiTotal, setAiTotal] = useState(0)

  const [stuckRows, setStuckRows] = useState<DebugAiStuckTicketRow[]>([])
  const [stuckMeta, setStuckMeta] = useState<{ total: number; min_hours: number; error?: string } | null>(null)

  const [reqRows, setReqRows] = useState<DebugAiRequestTicketRow[]>([])
  const [reqTotal, setReqTotal] = useState(0)

  const [blockRows, setBlockRows] = useState<DebugAutoResolveBlockRow[]>([])
  const [blockTotal, setBlockTotal] = useState(0)

  const [queueBlockRows, setQueueBlockRows] = useState<DebugAiQueueBlockRow[]>([])
  const [queueBlockTotal, setQueueBlockTotal] = useState(0)
  const savedQueueBlockFilters = readSavedQueueBlockFilters()
  const [queueBlockExcludeKinds, setQueueBlockExcludeKinds] = useState(savedQueueBlockFilters.exclude)
  const [queueBlockIncludeKinds, setQueueBlockIncludeKinds] = useState(savedQueueBlockFilters.include)
  const [queueBlockClearKinds, setQueueBlockClearKinds] = useState(savedQueueBlockFilters.clear)
  const [selectedQueueBlockIds, setSelectedQueueBlockIds] = useState<Set<string>>(() => new Set())
  const [clearingTicketId, setClearingTicketId] = useState<string | null>(null)
  const [clearingAllQueueBlocks, setClearingAllQueueBlocks] = useState(false)
  const [clearingBatchQueueBlocks, setClearingBatchQueueBlocks] = useState(false)

  const queueBlockFiltersRef = useRef({
    exclude: queueBlockExcludeKinds,
    include: queueBlockIncludeKinds,
    clear: queueBlockClearKinds,
  })
  queueBlockFiltersRef.current = {
    exclude: queueBlockExcludeKinds,
    include: queueBlockIncludeKinds,
    clear: queueBlockClearKinds,
  }

  const [dbSummary, setDbSummary] = useState<Record<string, number> | null>(null)
  const [dbByApp, setDbByApp] = useState<{ application_name: string; total: number; active: number; idle: number; idle_in_transaction: number }[]>([])
  const [dbConn, setDbConn] = useState<import('@/types/api').DebugDbConnectionRow[]>([])

  const [auditRows, setAuditRows] = useState<AIAuditLogRow[]>([])
  const [auditTotal, setAuditTotal] = useState(0)
  const [auditDays, setAuditDays] = useState(30)
  const [auditProjectId, setAuditProjectId] = useState('')
  const [auditUserId, setAuditUserId] = useState('')
  const [auditPreset, setAuditPreset] = useState('')
  const [auditSearch, setAuditSearch] = useState('')
  const [auditPage, setAuditPage] = useState(1)
  const auditLimit = 50
  const auditPageRef = useRef(auditPage)
  auditPageRef.current = auditPage
  const prevDebugTabRef = useRef<DebugTab>(activeTab)

  const auditFiltersRef = useRef({
    days: auditDays,
    projectId: auditProjectId,
    userId: auditUserId,
    preset: auditPreset,
    search: auditSearch,
  })
  auditFiltersRef.current = {
    days: auditDays,
    projectId: auditProjectId,
    userId: auditUserId,
    preset: auditPreset,
    search: auditSearch,
  }

  useEffect(() => {
    let alive = true
    void api.projects()
      .then((p) => {
        if (alive) setProjects(Array.isArray(p) ? p : [])
      })
      .catch(() => {
        if (alive) setProjects([])
      })
    void api.channelsList()
      .then((c) => {
        if (alive) setChannels(Array.isArray(c) ? c : [])
      })
      .catch(() => {
        if (alive) setChannels([])
      })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (!canAudit && !canDebug) return
    let alive = true
    void api.workspaceUsers()
      .then((u) => {
        if (alive) setUsers(Array.isArray(u) ? u : [])
      })
      .catch(() => {
        if (alive) setUsers([])
      })
    return () => {
      alive = false
    }
  }, [canAudit, canDebug])

  const openTicket = useCallback(
    (t: { id: string; ticket_number: number; project_slug?: string | null }) => {
      const ref = ticketRefParts(t)
      openOrSelectTab(`/tickets/${encodeURIComponent(ref)}`)
    },
    [openOrSelectTab],
  )

  const openTicketNewTab = useCallback((t: { id: string; ticket_number: number; project_slug?: string | null }) => {
    const path = `/tickets/${encodeURIComponent(ticketRefParts(t))}`
    window.open(path, '_blank', 'noopener,noreferrer')
  }, [])

  const loadAiList = useCallback(async () => {
    setLoadingTab('ai-list')
    setError(null)
    const f = debugFiltersRef.current
    try {
      const params: Record<string, string | number | undefined> = {
        project_id: f.projectId || undefined,
        status: f.status || undefined,
        page: pageRef.current,
        page_size: pageSize,
      }
      if (f.dateFrom && f.dateTo) {
        params.date_from = f.dateFrom
        params.date_to = f.dateTo
      } else {
        params.updated_within_hours = f.hours
      }
      const res = await api.debugAiTickets(params)
      setAiTickets(res.tickets ?? [])
      setAiTotal(typeof res.total === 'number' ? res.total : 0)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setAiTickets([])
      setAiTotal(0)
    } finally {
      setLoadingTab((tab) => (tab === 'ai-list' ? null : tab))
    }
  }, [pageSize])

  const loadStuck = useCallback(async () => {
    setLoadingTab('stuck')
    setError(null)
    const f = debugFiltersRef.current
    try {
      const res = await api.debugAiStuck({
        project_id: f.projectId || undefined,
        min_hours: f.hours,
      })
      const list = res.tickets ?? []
      setStuckRows(list.slice(0, 100))
      setStuckMeta({
        total: res.total ?? list.length,
        min_hours: res.min_hours ?? hours,
        error: res.error,
      })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setStuckRows([])
      setStuckMeta(null)
    } finally {
      setLoadingTab((tab) => (tab === 'stuck' ? null : tab))
    }
  }, [])

  const loadRequests = useCallback(async () => {
    setLoadingTab('ai-requests')
    setError(null)
    const f = debugFiltersRef.current
    try {
      const params: Record<string, string | number | undefined> = {
        project_id: f.projectId || undefined,
        status: f.status || undefined,
        page: pageRef.current,
        page_size: pageSize,
      }
      if (f.dateFrom && f.dateTo) {
        params.date_from = f.dateFrom
        params.date_to = f.dateTo
      } else {
        params.updated_within_hours = f.hours
      }
      const res = await api.debugAiRequests(params)
      setReqRows(res.tickets ?? [])
      setReqTotal(typeof res.total === 'number' ? res.total : 0)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setReqRows([])
      setReqTotal(0)
    } finally {
      setLoadingTab((tab) => (tab === 'ai-requests' ? null : tab))
    }
  }, [pageSize])

  const loadBlocks = useCallback(async () => {
    setLoadingTab('auto-resolve')
    setError(null)
    const f = debugFiltersRef.current
    try {
      const params: Record<string, string | number | undefined> = {
        project_id: f.projectId || undefined,
        page: pageRef.current,
        page_size: pageSize,
      }
      if (f.dateFrom && f.dateTo) {
        params.date_from = f.dateFrom
        params.date_to = f.dateTo
      } else {
        params.hours = f.hours
      }
      const res = await api.debugAutoResolveBlocks(params)
      setBlockRows(res.items ?? [])
      setBlockTotal(typeof res.total === 'number' ? res.total : 0)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setBlockRows([])
      setBlockTotal(0)
    } finally {
      setLoadingTab((tab) => (tab === 'auto-resolve' ? null : tab))
    }
  }, [pageSize])

  const loadQueueBlocks = useCallback(async () => {
    setLoadingTab('queue-blocks')
    setError(null)
    const f = debugFiltersRef.current
    const qf = queueBlockFiltersRef.current
    try {
      const res = await api.debugAiQueueBlocks({
        project_id: f.projectId || undefined,
        page: pageRef.current,
        page_size: pageSize,
        exclude_kinds: kindsToCsv(qf.exclude),
        include_kinds: kindsToCsv(qf.include),
      })
      setQueueBlockRows(res.items ?? [])
      setQueueBlockTotal(typeof res.total === 'number' ? res.total : 0)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setQueueBlockRows([])
      setQueueBlockTotal(0)
    } finally {
      setLoadingTab((tab) => (tab === 'queue-blocks' ? null : tab))
    }
  }, [pageSize])

  useEffect(() => {
    try {
      localStorage.setItem(
        QUEUE_BLOCK_FILTER_LS,
        JSON.stringify({
          exclude: queueBlockExcludeKinds,
          include: queueBlockIncludeKinds,
          clear: queueBlockClearKinds,
        }),
      )
    } catch {
      /* ignore */
    }
  }, [queueBlockExcludeKinds, queueBlockIncludeKinds, queueBlockClearKinds])

  useEffect(() => {
    if (activeTab !== 'queue-blocks') return
    pageRef.current = 1
    setPage(1)
    setSelectedQueueBlockIds(new Set())
  }, [queueBlockExcludeKinds, queueBlockIncludeKinds, activeTab])

  const notifyQueueClearResult = useCallback(
    (res: { cleared_tickets?: number; processed?: number; enqueued_count?: number; truncated?: boolean; errors?: string[] }, enqueue: boolean) => {
      const parts = [
        `Обработано: ${res.processed ?? 0}`,
        `Снято с тикетов: ${res.cleared_tickets ?? 0}`,
      ]
      if (enqueue) parts.push(`Поставлено в очередь AI: ${res.enqueued_count ?? 0}`)
      if (res.truncated) parts.push('Не все тикеты (лимит 500) — повторите при необходимости.')
      if (res.errors?.length) parts.push(`Ошибки: ${res.errors.slice(0, 3).join('; ')}`)
      showNotification({ title: 'Готово', message: parts.join('. '), color: 'green' })
    },
    [],
  )

  const clearQueueBlocks = useCallback(
    async (ticketId: string, enqueue: boolean) => {
      const body = blocksBodyForClear(queueBlockFiltersRef.current.clear)
      if (!body) {
        showNotification({
          title: 'Нет типов',
          message: 'Выберите хотя бы один тип блокировки для снятия.',
          color: 'yellow',
        })
        return
      }
      setClearingTicketId(ticketId)
      try {
        const res = await api.debugClearAiQueueBlocks(ticketId, { ...body, enqueue })
        if (!res.ok) {
          throw new ApiError(400, res.error || 'Не удалось снять блокировки')
        }
        const cleared = (res.cleared ?? []).join(', ') || '—'
        showNotification({
          title: 'Готово',
          message: enqueue
            ? `Снято: ${cleared}. ${res.enqueued ? 'Поставлено в очередь AI.' : 'В очередь не поставлено (метка enqueue).'}`
            : `Снято: ${cleared}.`,
          color: 'green',
        })
        setSelectedQueueBlockIds((prev) => {
          const next = new Set(prev)
          next.delete(ticketId)
          return next
        })
        void loadQueueBlocks()
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось снять блокировки',
          color: 'red',
        })
      } finally {
        setClearingTicketId(null)
      }
    },
    [loadQueueBlocks],
  )

  const clearSelectedQueueBlocks = useCallback(
    async (enqueue: boolean) => {
      const ids = [...selectedQueueBlockIds]
      if (ids.length === 0) return
      const body = blocksBodyForClear(queueBlockFiltersRef.current.clear)
      if (!body) {
        showNotification({
          title: 'Нет типов',
          message: 'Выберите хотя бы один тип блокировки для снятия.',
          color: 'yellow',
        })
        return
      }
      const label = enqueue
        ? `Снять блокировки и поставить в очередь для ${ids.length} обращений?`
        : `Снять блокировки для ${ids.length} обращений?`
      if (!window.confirm(label)) return

      setClearingBatchQueueBlocks(true)
      try {
        const res = await api.debugClearBatchAiQueueBlocks({ ticket_ids: ids, ...body, enqueue })
        if (!res.ok) throw new ApiError(400, 'Не удалось снять блокировки')
        notifyQueueClearResult(res, enqueue)
        setSelectedQueueBlockIds(new Set())
        void loadQueueBlocks()
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось снять блокировки',
          color: 'red',
        })
      } finally {
        setClearingBatchQueueBlocks(false)
      }
    },
    [loadQueueBlocks, notifyQueueClearResult, selectedQueueBlockIds],
  )

  const clearAllQueueBlocks = useCallback(
    async (enqueue: boolean) => {
      const f = debugFiltersRef.current
      const qf = queueBlockFiltersRef.current
      const body = blocksBodyForClear(qf.clear)
      if (!body) {
        showNotification({
          title: 'Нет типов',
          message: 'Выберите хотя бы один тип блокировки для снятия.',
          color: 'yellow',
        })
        return
      }
      const label = enqueue ? 'Снять все блокировки и поставить в очередь?' : 'Снять все блокировки?'
      const scope = f.projectId ? ' по выбранному проекту' : ' по всему воркспейсу'
      if (!window.confirm(`${label}\n\nБудут обработаны до 500 тикетов${scope} (с учётом фильтров списка).`)) return

      setClearingAllQueueBlocks(true)
      try {
        const res = await api.debugClearAllAiQueueBlocks(
          {
            project_id: f.projectId || undefined,
            exclude_kinds: kindsToCsv(qf.exclude),
            include_kinds: kindsToCsv(qf.include),
          },
          { enqueue, ...body },
        )
        if (!res.ok) throw new ApiError(400, 'Не удалось снять блокировки')
        notifyQueueClearResult(res, enqueue)
        setSelectedQueueBlockIds(new Set())
        void loadQueueBlocks()
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось снять блокировки',
          color: 'red',
        })
      } finally {
        setClearingAllQueueBlocks(false)
      }
    },
    [loadQueueBlocks, notifyQueueClearResult],
  )

  const loadDb = useCallback(async () => {
    setLoadingTab('db')
    setError(null)
    try {
      const res = await api.debugDbConnections()
      setDbSummary(res.summary ?? null)
      setDbByApp(res.by_application ?? [])
      setDbConn(res.connections ?? [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setDbSummary(null)
      setDbByApp([])
      setDbConn([])
    } finally {
      setLoadingTab((tab) => (tab === 'db' ? null : tab))
    }
  }, [])

  const loadAudit = useCallback(async () => {
    setLoadingTab('ai-audit')
    setError(null)
    const a = auditFiltersRef.current
    try {
      const res = await api.aiAuditLogs({
        days: a.days,
        project_id: a.projectId || undefined,
        user_id: a.userId || undefined,
        action_group: a.preset || undefined,
        search: a.search.trim() || undefined,
        limit: auditLimit,
        offset: (auditPageRef.current - 1) * auditLimit,
      })
      setAuditRows(res.items ?? [])
      setAuditTotal(typeof res.total_count === 'number' ? res.total_count : 0)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setAuditRows([])
      setAuditTotal(0)
    } finally {
      setLoadingTab((tab) => (tab === 'ai-audit' ? null : tab))
    }
  }, [auditLimit])

  const reload = useCallback(() => {
    if (activeTab === 'ai-list') void loadAiList()
    else if (activeTab === 'stuck') void loadStuck()
    else if (activeTab === 'ai-requests') void loadRequests()
    else if (activeTab === 'auto-resolve') void loadBlocks()
    else if (activeTab === 'queue-blocks') void loadQueueBlocks()
    else if (activeTab === 'db') void loadDb()
    else if (activeTab === 'ai-audit') void loadAudit()
  }, [activeTab, loadAiList, loadStuck, loadRequests, loadBlocks, loadQueueBlocks, loadDb, loadAudit])

  useEffect(() => {
    if (!canEnter) return
    if (prevDebugTabRef.current !== activeTab) {
      prevDebugTabRef.current = activeTab
      pageRef.current = 1
      auditPageRef.current = 1
      setPage(1)
      setAuditPage(1)
    }
    if (activeTab === 'ai-list') void loadAiList()
    else if (activeTab === 'stuck') void loadStuck()
    else if (activeTab === 'ai-requests') void loadRequests()
    else if (activeTab === 'auto-resolve') void loadBlocks()
    else if (activeTab === 'queue-blocks') void loadQueueBlocks()
    else if (activeTab === 'db') void loadDb()
    else if (activeTab === 'ai-audit') void loadAudit()
    else if (
      activeTab === 'failed-deliveries' ||
      activeTab === 'telegram-backfill' ||
      activeTab === 'email-backfill' ||
      activeTab === 'tickets-export' ||
      activeTab === 'loadtest' ||
      activeTab === 'shift-breaks'
    ) {
      setLoadingTab(null)
      setError(null)
    }
  }, [
    canEnter,
    activeTab,
    page,
    pageSize,
    auditPage,
    loadAiList,
    loadStuck,
    loadRequests,
    loadBlocks,
    loadQueueBlocks,
    loadDb,
    loadAudit,
    queueBlockExcludeKinds,
    queueBlockIncludeKinds,
  ])

  const applyFilters = () => {
    pageRef.current = 1
    auditPageRef.current = 1
    setPage(1)
    setAuditPage(1)
    queueMicrotask(() => reload())
  }

  const onPageSizeChange = (v: string | null) => {
    const n = parseInt(String(v), 10)
    if (!PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return
    try {
      localStorage.setItem(PAGE_SIZE_LS, String(n))
    } catch {
      /* ignore */
    }
    setPageSize(n)
    setPage(1)
  }

  const releaseAi = useCallback(
    async (ticketId: string) => {
      try {
        await api.ticketUpdate(ticketId, { status: 'open', assigned_to_id: null })
        showNotification({ title: 'Готово', message: 'Обращение снято с Мозгобота.', color: 'green' })
        void loadAiList()
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось обновить',
          color: 'red',
        })
      }
    },
    [loadAiList],
  )

  const exportAuditCsv = useCallback(async () => {
    try {
      const blob = await api.aiAuditExportCsv({
        days: auditDays,
        project_id: auditProjectId || undefined,
        user_id: auditUserId || undefined,
        action_group: auditPreset || undefined,
        search: auditSearch.trim() || undefined,
        export_limit: 100_000,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ai_audit_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`
      a.click()
      URL.revokeObjectURL(url)
      showNotification({ title: 'Экспорт', message: 'Файл сохранён.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось экспортировать',
        color: 'red',
      })
    }
  }, [auditDays, auditProjectId, auditUserId, auditPreset, auditSearch])

  const runTelegramBackfill = useCallback(
    async (apply: boolean) => {
      if (!telegramBackfillChannelId) {
        showNotification({ title: 'Канал не выбран', message: 'Выберите Telegram-канал для догрузки.', color: 'yellow' })
        return
      }
      const ok = window.confirm(
        `${apply ? 'Догрузить' : 'Проверить'} pending updates из Telegram Bot API?\n\nНа время операции бот канала будет остановлен, затем запущен обратно. Telegram отдаёт только неподтверждённые updates.`,
      )
      if (!ok) return
      setTelegramBackfillLoading(true)
      setTelegramBackfillResult(null)
      try {
        const res = await api.debugTelegramBackfill({
          channel_id: telegramBackfillChannelId,
          apply,
          limit: telegramBackfillLimit,
          max_batches: telegramBackfillMaxBatches,
          allowed_today_only: telegramBackfillTodayOnly,
        })
        setTelegramBackfillResult(res)
        showNotification({
          title: apply ? 'Догрузка завершена' : 'Проверка завершена',
          message: `Найдено: ${res.updates_seen}. Загружено: ${res.updates_processed}. Ошибок: ${res.errors?.length ?? 0}.`,
          color: res.errors?.length ? 'yellow' : 'green',
        })
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось получить updates из Telegram',
          color: 'red',
        })
      } finally {
        setTelegramBackfillLoading(false)
      }
    },
    [
      telegramBackfillChannelId,
      telegramBackfillLimit,
      telegramBackfillMaxBatches,
      telegramBackfillTodayOnly,
    ],
  )

  const loadTicketWorkspaceRepairList = useCallback(
    async (page = ticketWorkspaceRepairPage) => {
      if (!telegramBackfillChannelId) {
        showNotification({ title: 'Канал не выбран', message: 'Выберите Telegram-канал.', color: 'yellow' })
        return
      }
      setTicketWorkspaceRepairListBusy(true)
      try {
        const res = await api.channelListTicketWorkspaceRepairs(telegramBackfillChannelId, page, 20)
        setTicketWorkspaceRepairPage(res.page)
        setTicketWorkspaceRepairList(res)
      } catch (e) {
        showNotification({
          title: 'Список обращений',
          message: e instanceof ApiError ? e.message : 'Не удалось загрузить список',
          color: 'red',
        })
      } finally {
        setTicketWorkspaceRepairListBusy(false)
      }
    },
    [telegramBackfillChannelId, ticketWorkspaceRepairPage],
  )

  const runTicketWorkspaceRepair = useCallback(
    async (apply: boolean) => {
      if (!telegramBackfillChannelId) {
        showNotification({ title: 'Канал не выбран', message: 'Выберите Telegram-канал.', color: 'yellow' })
        return
      }
      setTicketWorkspaceRepairBusy(true)
      try {
        const res = await api.channelRepairTicketWorkspaces(telegramBackfillChannelId, { apply, fix_customers: true })
        setTicketWorkspaceRepairResult(res)
        await loadTicketWorkspaceRepairList(1)
        showNotification({
          title: apply ? 'Workspace тикетов исправлен' : 'Проверка workspace тикетов',
          message: apply
            ? `Тикетов: ${res.tickets_updated}, клиентов: ${res.customers_updated}, перенумеровано: ${res.tickets_renumbered_due_to_conflict}`
            : `К исправлению: ${res.tickets_to_fix}, клиентов: ${res.customers_to_fix}`,
          color: apply || res.tickets_to_fix === 0 ? 'green' : 'yellow',
        })
      } catch (e) {
        showNotification({
          title: 'Workspace тикетов',
          message: e instanceof ApiError ? e.message : 'Не удалось выполнить исправление',
          color: 'red',
        })
      } finally {
        setTicketWorkspaceRepairBusy(false)
      }
    },
    [telegramBackfillChannelId, loadTicketWorkspaceRepairList],
  )

  const repairOneTicketWorkspace = useCallback(
    async (ticketId: string) => {
      if (!telegramBackfillChannelId) return
      setTicketWorkspaceRepairSingleBusy(ticketId)
      try {
        const res = await api.channelRepairSingleTicketWorkspace(telegramBackfillChannelId, ticketId, { fix_customers: true })
        await loadTicketWorkspaceRepairList(ticketWorkspaceRepairPage)
        showNotification({
          title: res.already_correct ? 'Уже исправлено' : 'Обращение исправлено',
          message: res.renumbered_due_to_conflict
            ? `Номер изменён: ${res.old_ticket_number} → ${res.new_ticket_number}`
            : `Номер обращения: ${res.new_ticket_number}`,
          color: 'green',
        })
      } catch (e) {
        showNotification({
          title: 'Обращение',
          message: e instanceof ApiError ? e.message : 'Не удалось исправить обращение',
          color: 'red',
        })
      } finally {
        setTicketWorkspaceRepairSingleBusy(null)
      }
    },
    [telegramBackfillChannelId, loadTicketWorkspaceRepairList, ticketWorkspaceRepairPage],
  )

  const loadTelegramIngestedTickets = useCallback(
    async (page = telegramIngestedPage) => {
      if (!telegramBackfillChannelId) {
        showNotification({ title: 'Канал не выбран', message: 'Выберите Telegram-канал.', color: 'yellow' })
        return
      }
      setTelegramIngestedBusy(true)
      try {
        const res = await api.channelTelegramIngestedTickets(
          telegramBackfillChannelId,
          page,
          20,
          telegramIngestedSearch,
        )
        setTelegramIngestedPage(res.page)
        setTelegramIngestedList(res)
      } catch (e) {
        showNotification({
          title: 'Telegram обращения',
          message: e instanceof ApiError ? e.message : 'Не удалось загрузить обращения',
          color: 'red',
        })
      } finally {
        setTelegramIngestedBusy(false)
      }
    },
    [telegramBackfillChannelId, telegramIngestedPage, telegramIngestedSearch],
  )

  const projectSelectData = useMemo(() => {
    const opts = [{ value: '', label: 'Все проекты' }]
    for (const p of projects) {
      opts.push({ value: p.id, label: p.name || p.slug || p.id })
    }
    return opts
  }, [projects])

  const telegramChannelSelectData = useMemo(() => {
    const rows = channels
      .filter((c) => String(c.channel_type || '').toLowerCase() === 'telegram')
      .sort((a, b) => {
        const activeDiff = Number(b.is_active !== false) - Number(a.is_active !== false)
        if (activeDiff) return activeDiff
        return String(a.name || a.id).localeCompare(String(b.name || b.id), 'ru')
      })
    return rows.map((c) => ({ value: c.id, label: `${c.name || c.id}${c.is_active === false ? ' (выключен)' : ''}` }))
  }, [channels])

  useEffect(() => {
    if (!telegramBackfillChannelId && telegramChannelSelectData[0]) {
      setTelegramBackfillChannelId(telegramChannelSelectData[0].value)
    }
  }, [telegramBackfillChannelId, telegramChannelSelectData])

  useEffect(() => {
    setTelegramIngestedList(null)
    setTelegramIngestedPage(1)
    setTicketWorkspaceRepairList(null)
    setTicketWorkspaceRepairPage(1)
  }, [telegramBackfillChannelId])

  useEffect(() => {
    if (activeTab !== 'telegram-backfill') return
    if (!telegramBackfillChannelId || telegramIngestedList || telegramIngestedBusy) return
    void loadTelegramIngestedTickets(1)
  }, [
    activeTab,
    telegramBackfillChannelId,
    telegramIngestedList,
    telegramIngestedBusy,
    loadTelegramIngestedTickets,
  ])

  const userSelectData = useMemo(() => {
    const opts = [{ value: '', label: 'Все пользователи' }]
    for (const u of users) {
      const label =
        [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || u.id
      opts.push({ value: u.id, label })
    }
    return opts
  }, [users])

  const pagesAi = Math.max(1, Math.ceil(aiTotal / pageSize))
  const pagesReq = Math.max(1, Math.ceil(reqTotal / pageSize))
  const pagesBlocks = Math.max(1, Math.ceil(blockTotal / pageSize))
  const pagesQueueBlocks = Math.max(1, Math.ceil(queueBlockTotal / pageSize))
  const pagesAudit = Math.max(1, Math.ceil(auditTotal / auditLimit))

  const queueBlocksBusy =
    clearingAllQueueBlocks || clearingBatchQueueBlocks || clearingTicketId != null
  const canClearQueueBlockKinds = queueBlockClearKinds.length > 0
  const selectedQueueBlockCount = selectedQueueBlockIds.size
  const allQueueBlocksPageSelected =
    queueBlockRows.length > 0 && queueBlockRows.every((r) => selectedQueueBlockIds.has(r.ticket_id))

  const toggleQueueBlockSelection = useCallback((ticketId: string, checked: boolean) => {
    setSelectedQueueBlockIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(ticketId)
      else next.delete(ticketId)
      return next
    })
  }, [])

  const toggleSelectAllQueueBlocksPage = useCallback(() => {
    setSelectedQueueBlockIds((prev) => {
      const next = new Set(prev)
      if (allQueueBlocksPageSelected) {
        for (const r of queueBlockRows) next.delete(r.ticket_id)
      } else {
        for (const r of queueBlockRows) next.add(r.ticket_id)
      }
      return next
    })
  }, [allQueueBlocksPageSelected, queueBlockRows])

  const showDebugFilters =
    activeTab === 'ai-list' ||
    activeTab === 'stuck' ||
    activeTab === 'ai-requests' ||
    activeTab === 'auto-resolve' ||
    activeTab === 'queue-blocks'
  const showHoursDateFilters = activeTab !== 'stuck' && activeTab !== 'queue-blocks'
  const showStatus = activeTab === 'ai-list' || activeTab === 'ai-requests'
  const showPagination =
    activeTab === 'ai-list' ||
    activeTab === 'ai-requests' ||
    activeTab === 'auto-resolve' ||
    activeTab === 'queue-blocks'

  if (!canEnter) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          У вас нет права на отладку или AI-аудит.
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconBug size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Отладка
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Диагностика Мозгобота, очередей, доставок, импортов, базы данных и AI-аудита.
              </Text>
            </Box>
          }
        />
        <Tabs value={activeTab} onChange={setTab}>
          <Tabs.List>
            {canDebug ? (
              <>
                <Tabs.Tab value="ai-list">Список обращений</Tabs.Tab>
                <Tabs.Tab value="stuck">Зависшие</Tabs.Tab>
                <Tabs.Tab value="ai-requests">Запросы в ИИ</Tabs.Tab>
                <Tabs.Tab value="auto-resolve">Блокировки авто-resolve</Tabs.Tab>
                <Tabs.Tab value="queue-blocks">Блокировки очереди AI</Tabs.Tab>
                <Tabs.Tab value="slow-first-response">Первый ответ &gt; 10 мин</Tabs.Tab>
                <Tabs.Tab value="pending-history">История ожидания</Tabs.Tab>
                <Tabs.Tab value="mozgobot-report">Отчёт Мозгобот</Tabs.Tab>
                <Tabs.Tab value="failed-deliveries">Недоставленные</Tabs.Tab>
                <Tabs.Tab value="telegram-backfill">Telegram догруз</Tabs.Tab>
                <Tabs.Tab value="email-backfill">Email догруз</Tabs.Tab>
                <Tabs.Tab value="tickets-export">Экспорт тикетов</Tabs.Tab>
                <Tabs.Tab value="db">БД</Tabs.Tab>
                {isOwnerOrAdmin ? <Tabs.Tab value="loadtest">Нагрузка Мозгобота</Tabs.Tab> : null}
                {isOwnerOrAdmin ? <Tabs.Tab value="shift-breaks">Перерывы смен</Tabs.Tab> : null}
              </>
            ) : null}
            {canAudit ? <Tabs.Tab value="ai-audit">AI аудит</Tabs.Tab> : null}
          </Tabs.List>

          {showDebugFilters ? (
            <Card withBorder padding="sm" radius="md" mt="sm">
              <Stack gap="sm">
                <Text size="xs" c="dimmed">
                  {activeTab === 'stuck'
                    ? 'Поле «часов» — минимальный возраст последнего «благодарственного» сообщения клиента (как в classic).'
                    : activeTab === 'queue-blocks'
                      ? 'Тикеты с блокировками очереди Мозгобота (БД и Redis). Снятие разблокирует повторную постановку в Celery.'
                      : 'Обращения с участием Мозгобота; интервал дат имеет приоритет над «часами».'}
                </Text>
                <Group align="flex-end" wrap="wrap" gap="sm">
                  {activeTab === 'stuck' || showHoursDateFilters ? (
                    <NumberInput
                      size="xs"
                      label={activeTab === 'stuck' ? 'Мин. часов' : 'Часов'}
                      value={hours}
                      onChange={(v) => setHours(typeof v === 'number' ? v : 24)}
                      min={1}
                      max={720}
                      w={90}
                    />
                  ) : null}
                  {showHoursDateFilters ? (
                    <>
                      <TextInput
                        size="xs"
                        type="date"
                        label="С даты"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.currentTarget.value)}
                        w={150}
                      />
                      <TextInput
                        size="xs"
                        type="date"
                        label="По дату"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.currentTarget.value)}
                        w={150}
                      />
                      <Group gap={4}>
                        <Button
                          size="compact-xs"
                          variant="light"
                          onClick={() => {
                            const { from, to } = debugDatePreset('yesterday')
                            setDateFrom(from)
                            setDateTo(to)
                          }}
                        >
                          Вчера
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="light"
                          onClick={() => {
                            const { from, to } = debugDatePreset('last7')
                            setDateFrom(from)
                            setDateTo(to)
                          }}
                        >
                          7 дн.
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="light"
                          onClick={() => {
                            const { from, to } = debugDatePreset('last_week')
                            setDateFrom(from)
                            setDateTo(to)
                          }}
                        >
                          Пн–Вс
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="light"
                          onClick={() => {
                            const { from, to } = debugDatePreset('thu_fri')
                            setDateFrom(from)
                            setDateTo(to)
                          }}
                        >
                          Чт–Пт
                        </Button>
                      </Group>
                    </>
                  ) : null}
                  <Select
                    size="xs"
                    label="Проект"
                    data={projectSelectData}
                    value={projectId}
                    onChange={(value) => setProjectId(value || '')}
                    searchable
                    allowDeselect={false}
                    checkIconPosition="right"
                    styles={{ root: { width: 220 } }}
                  />
                  {showStatus ? (
                    <Select
                      size="xs"
                      label="Статус"
                      data={STATUS_OPTIONS}
                      value={status}
                      onChange={(value) => setStatus(value || '')}
                      allowDeselect={false}
                      checkIconPosition="right"
                      styles={{ root: { width: 160 } }}
                    />
                  ) : null}
                  <Button size="xs" onClick={applyFilters}>
                    Применить
                  </Button>
                  <ActionIcon variant="default" size="md" aria-label="Обновить" onClick={() => reload()}>
                    <IconRefresh size={16} stroke={1.5} />
                  </ActionIcon>
                </Group>
              </Stack>
            </Card>
          ) : null}

          {activeTab === 'ai-audit' ? (
            <Card withBorder padding="sm" radius="md" mt="sm">
              <Stack gap="sm">
                <Group align="flex-end" wrap="wrap" gap="sm">
                  <Select
                    size="xs"
                    label="Период"
                    data={[
                      { value: '7', label: '7 дней' },
                      { value: '30', label: '30 дней' },
                      { value: '90', label: '90 дней' },
                      { value: '180', label: '180 дней' },
                    ]}
                    value={String(auditDays)}
                    onChange={(value) => setAuditDays(parseInt(value || '30', 10) || 30)}
                    allowDeselect={false}
                    checkIconPosition="right"
                    styles={{ root: { width: 130 } }}
                  />
                  <Select
                    size="xs"
                    label="Проект"
                    data={projectSelectData}
                    value={auditProjectId}
                    onChange={(value) => setAuditProjectId(value || '')}
                    searchable
                    allowDeselect={false}
                    checkIconPosition="right"
                    styles={{ root: { width: 220 } }}
                  />
                  <Select
                    size="xs"
                    label="Пользователь"
                    data={userSelectData}
                    value={auditUserId}
                    onChange={(value) => setAuditUserId(value || '')}
                    searchable
                    allowDeselect={false}
                    checkIconPosition="right"
                    styles={{ root: { width: 220 } }}
                  />
                  <Select
                    size="xs"
                    label="Пресет"
                    data={[
                      { value: '', label: 'Все действия' },
                      { value: 'excel', label: 'Excel / Upload' },
                      { value: 'delete', label: 'Удаления' },
                      { value: 'sync', label: 'Sync' },
                    ]}
                    value={auditPreset}
                    onChange={(value) => setAuditPreset(value || '')}
                    allowDeselect={false}
                    checkIconPosition="right"
                    styles={{ root: { width: 170 } }}
                  />
                  <TextInput
                    size="xs"
                    label="Поиск"
                    placeholder="Описание / детали"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.currentTarget.value)}
                    style={{ flex: '1 1 180px', minWidth: 140 }}
                  />
                  <Button
                    size="xs"
                    onClick={() => {
                      auditPageRef.current = 1
                      setAuditPage(1)
                      void loadAudit()
                    }}
                  >
                    Применить
                  </Button>
                  <Button size="xs" variant="light" onClick={exportAuditCsv} leftSection={<IconDownload size={14} />}>
                    CSV
                  </Button>
                  <ActionIcon variant="default" size="md" aria-label="Обновить" onClick={() => void loadAudit()}>
                    <IconRefresh size={16} stroke={1.5} />
                  </ActionIcon>
                </Group>
              </Stack>
            </Card>
          ) : null}

          {activeTab === 'db' ? (
            <Group mt="sm">
              <Button size="xs" leftSection={<IconRefresh size={14} />} onClick={() => void loadDb()}>
                Обновить
              </Button>
            </Group>
          ) : null}

          {error ? (
            <Alert color="red" variant="light" title="Ошибка">
              {error}
            </Alert>
          ) : null}

          {loading &&
          activeTab !== 'ai-list' &&
          activeTab !== 'ai-requests' &&
          activeTab !== 'auto-resolve' &&
          activeTab !== 'queue-blocks' &&
          activeTab !== 'failed-deliveries' &&
          activeTab !== 'telegram-backfill' &&
          activeTab !== 'email-backfill' &&
          activeTab !== 'tickets-export' &&
          activeTab !== 'ai-audit' &&
          activeTab !== 'loadtest' &&
          activeTab !== 'shift-breaks' ? (
            <PageCenterLoader label="Загрузка" minHeight={120} />
          ) : null}

          <Tabs.Panel value="ai-list" pt="sm">
            {showPagination ? (
              <Group justify="space-between" mb="sm" wrap="wrap">
                <Text size="xs" c="dimmed">
                  Всего: <strong>{aiTotal}</strong>
                </Text>
                <Group gap="xs" wrap="wrap" align="center">
                  <NativeSelect
                    size="xs"
                    data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                    value={String(pageSize)}
                    onChange={(e) => onPageSizeChange(e.currentTarget.value)}
                    w={72}
                  />
                  {pagesAi > 1 ? (
                    <PaginationBar page={page} pageCount={pagesAi} total={aiTotal} disabled={loading} onPageChange={setPage} />
                  ) : null}
                </Group>
              </Group>
            ) : null}
            {loading && activeTab === 'ai-list' ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}
            {!loading && activeTab === 'ai-list' && aiTickets.length === 0 ? (
              <EmptyState title="Пусто" description="Нет обращений по фильтру." />
            ) : null}
            {!loading && aiTickets.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>Обращение</DataTh>
                      <DataTh style={{ minWidth: 200 }}>Последнее от Мозгобота</DataTh>
                      <DataTh>Статус</DataTh>
                      <DataTh variantNarrow>Всего</DataTh>
                      <DataTh variantNarrow>Клиент</DataTh>
                      <DataTh variantNarrow>АИ</DataTh>
                      <DataTh variantNarrow>Самм.</DataTh>
                      <DataTh>Создан</DataTh>
                      <DataTh>Обновлён</DataTh>
                      <DataTh>Действие</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {aiTickets.map((t) => {
                      const diff = (t.messages_from_ai ?? 0) - (t.messages_from_customer ?? 0)
                      const summ = t.summaries_count ?? 0
                      const rowBg =
                        diff >= 3
                          ? 'rgba(200, 40, 30, 0.08)'
                          : diff === 1
                            ? 'rgba(220, 80, 60, 0.08)'
                            : summ > 1
                              ? 'rgba(255, 220, 80, 0.1)'
                              : undefined
                      const aiCellBg =
                        diff === 1 ? 'rgba(220, 80, 60, 0.35)' : diff >= 3 ? 'rgba(200, 40, 30, 0.55)' : undefined
                      const summBg = summ > 1 ? 'rgba(240, 200, 60, 0.45)' : undefined
                      return (
                        <DataTr key={t.id} style={{ background: rowBg }}>
                          <DataTd>
                            <Group gap={4} wrap="nowrap">
                              <Text
                                component="button"
                                type="button"
                                size="xs"
                                c="blue"
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                                onClick={() => openTicket(t)}
                              >
                                {t.project_slug ? `${t.project_slug}-${t.ticket_number}` : `#${t.ticket_number}`}
                              </Text>
                              <ActionIcon size="sm" variant="subtle" aria-label="В новой вкладке" onClick={() => openTicketNewTab(t)}>
                                <IconExternalLink size={14} />
                              </ActionIcon>
                            </Group>
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {t.subject || '—'}
                            </Text>
                          </DataTd>
                          <DataTd>
                            <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {(t.last_ai_message_content || '—').trim() || '—'}
                            </Text>
                          </DataTd>
                          <DataTd clip>{t.status}</DataTd>
                          <DataTd style={{ textAlign: 'center' }}>{t.messages_total ?? 0}</DataTd>
                          <DataTd style={{ textAlign: 'center' }}>{t.messages_from_customer ?? 0}</DataTd>
                          <DataTd style={{ textAlign: 'center', background: aiCellBg }}>{t.messages_from_ai ?? 0}</DataTd>
                          <DataTd style={{ textAlign: 'center', background: summBg }}>{summ}</DataTd>
                          <DataTd muted>
                            <Text size="xs">{formatDateTimeDot(t.created_at)}</Text>
                          </DataTd>
                          <DataTd muted>
                            <Text size="xs">{formatDateTimeDot(t.updated_at)}</Text>
                          </DataTd>
                          <DataTd>
                            <Button size="compact-xs" variant="light" onClick={() => void releaseAi(t.id)}>
                              Снять с АИ
                            </Button>
                          </DataTd>
                        </DataTr>
                      )
                    })}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Tabs.Panel>

          <Tabs.Panel value="stuck" pt="sm">
            {stuckMeta ? (
              <Stack gap={4} mb="sm">
                <Text size="sm">
                  Найдено потенциально зависших: <strong>{stuckMeta.total}</strong> (показано до 100). Критерий: последнее
                  сообщение клиента — благодарность/смайл, старше <strong>{stuckMeta.min_hours}</strong> ч.
                </Text>
                {stuckMeta.error ? (
                  <Text size="xs" c="orange">
                    {stuckMeta.error}
                  </Text>
                ) : null}
              </Stack>
            ) : null}
            {loading && activeTab === 'stuck' ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}
            {!loading && stuckRows.length === 0 ? (
              <EmptyState title="Пусто" description="Нет подходящих обращений." />
            ) : null}
            {stuckRows.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>Обращение</DataTh>
                      <DataTh>Последнее от клиента</DataTh>
                      <DataTh>Статус</DataTh>
                      <DataTh>Создан</DataTh>
                      <DataTh>Обновлён</DataTh>
                      <DataTh>Время сообщ. клиента</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {stuckRows.map((t) => (
                      <DataTr key={t.id}>
                        <DataTd>
                          <Group gap={4} wrap="nowrap">
                            <Text
                              component="button"
                              type="button"
                              size="xs"
                              c="blue"
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                              onClick={() => openTicket(t)}
                            >
                              {t.project_slug ? `${t.project_slug}-${t.ticket_number}` : `#${t.ticket_number}`}
                            </Text>
                            <ActionIcon size="sm" variant="subtle" aria-label="В новой вкладке" onClick={() => openTicketNewTab(t)}>
                              <IconExternalLink size={14} />
                            </ActionIcon>
                          </Group>
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {t.subject || '—'}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {(t.last_customer_message_content || '—').trim() || '—'}
                          </Text>
                        </DataTd>
                        <DataTd clip>{t.status}</DataTd>
                        <DataTd muted>
                          <Text size="xs">{formatDateTimeDot(t.created_at)}</Text>
                        </DataTd>
                        <DataTd muted>
                          <Text size="xs">{formatDateTimeDot(t.updated_at)}</Text>
                        </DataTd>
                        <DataTd muted>
                          <Text size="xs">{t.last_customer_message_at ? formatDateTimeDot(t.last_customer_message_at) : '—'}</Text>
                        </DataTd>
                      </DataTr>
                    ))}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Tabs.Panel>

          <Tabs.Panel value="ai-requests" pt="sm">
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Text size="xs" c="dimmed">
                Всего: <strong>{reqTotal}</strong>
              </Text>
              <Group gap="xs" wrap="wrap" align="center">
                <NativeSelect
                  size="xs"
                  data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                  value={String(pageSize)}
                  onChange={(e) => onPageSizeChange(e.currentTarget.value)}
                  w={72}
                />
                {pagesReq > 1 ? (
                  <PaginationBar page={page} pageCount={pagesReq} total={reqTotal} disabled={loading} onPageChange={setPage} />
                ) : null}
              </Group>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              Подсветка: запросов к ИИ ≥ 2 и строго больше числа ответов АИ.
            </Text>
            {loading && activeTab === 'ai-requests' ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}
            {!loading && reqRows.length === 0 ? <EmptyState title="Пусто" description="Нет данных." /> : null}
            {reqRows.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>Обращение</DataTh>
                      <DataTh>Статус</DataTh>
                      <DataTh variantNarrow>Запросов ИИ</DataTh>
                      <DataTh variantNarrow>Сообщ. АИ</DataTh>
                      <DataTh>Создан</DataTh>
                      <DataTh>Обновлён</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {reqRows.map((t) => {
                      const ar = t.ai_request_count ?? 0
                      const am = t.ai_messages_total ?? 0
                      const hi = ar >= 2 && ar > am
                      return (
                        <DataTr key={t.id} style={{ background: hi ? 'rgba(200, 40, 30, 0.1)' : undefined }}>
                          <DataTd>
                            <Group gap={4} wrap="nowrap">
                              <Text
                                component="button"
                                type="button"
                                size="xs"
                                c="blue"
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                onClick={() => openTicket(t)}
                              >
                                {t.project_slug ? `${t.project_slug}-${t.ticket_number}` : `#${t.ticket_number}`}
                              </Text>
                              <ActionIcon size="sm" variant="subtle" aria-label="В новой вкладке" onClick={() => openTicketNewTab(t)}>
                                <IconExternalLink size={14} />
                              </ActionIcon>
                            </Group>
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {t.subject || '—'}
                            </Text>
                          </DataTd>
                          <DataTd clip>{t.status}</DataTd>
                          <DataTd style={{ textAlign: 'center', background: hi ? 'rgba(200, 40, 30, 0.65)' : undefined, color: hi ? '#fff' : undefined }}>
                            {ar}
                          </DataTd>
                          <DataTd style={{ textAlign: 'center' }}>{am}</DataTd>
                          <DataTd muted>
                            <Text size="xs">{formatDateTimeDot(t.created_at)}</Text>
                          </DataTd>
                          <DataTd muted>
                            <Text size="xs">{formatDateTimeDot(t.updated_at)}</Text>
                          </DataTd>
                        </DataTr>
                      )
                    })}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Tabs.Panel>

          <Tabs.Panel value="auto-resolve" pt="sm">
            <Text size="xs" c="dimmed" mb="sm">
              Журнал блокировок авто-resolve. Фильтр по статусу обращения не применяется.
            </Text>
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Text size="xs" c="dimmed">
                Всего: <strong>{blockTotal}</strong>
              </Text>
              <Group gap="xs" wrap="wrap" align="center">
                <NativeSelect
                  size="xs"
                  data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                  value={String(pageSize)}
                  onChange={(e) => onPageSizeChange(e.currentTarget.value)}
                  w={72}
                />
                {pagesBlocks > 1 ? (
                  <PaginationBar page={page} pageCount={pagesBlocks} total={blockTotal} disabled={loading} onPageChange={setPage} />
                ) : null}
              </Group>
            </Group>
            {loading && activeTab === 'auto-resolve' ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}
            {!loading && blockRows.length === 0 ? <EmptyState title="Пусто" description="Нет записей." /> : null}
            {blockRows.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>Время</DataTh>
                      <DataTh>Обращение</DataTh>
                      <DataTh>Проект</DataTh>
                      <DataTh>Тип</DataTh>
                      <DataTh>Причина</DataTh>
                      <DataTh>Фрагмент</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {blockRows.map((r) => (
                      <DataTr key={r.id}>
                        <DataTd muted>
                          <Text size="xs">{r.created_at ? formatDateTimeDot(r.created_at) : '—'}</Text>
                        </DataTd>
                        <DataTd>
                          {r.ticket_id ? (
                            <Group gap={4} wrap="nowrap">
                              <Text
                                component="button"
                                type="button"
                                size="xs"
                                c="blue"
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                onClick={() =>
                                  openTicket({
                                    id: r.ticket_id!,
                                    ticket_number: r.ticket_number ?? 0,
                                    project_slug: null,
                                  })
                                }
                              >
                                {r.ticket_number != null ? `#${r.ticket_number}` : r.ticket_id.slice(0, 8)}
                              </Text>
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                aria-label="В новой вкладке"
                                onClick={() =>
                                  openTicketNewTab({
                                    id: r.ticket_id!,
                                    ticket_number: r.ticket_number ?? 0,
                                    project_slug: null,
                                  })
                                }
                              >
                                <IconExternalLink size={14} />
                              </ActionIcon>
                            </Group>
                          ) : (
                            '—'
                          )}
                        </DataTd>
                        <DataTd clip>{r.project_name || '—'}</DataTd>
                        <DataTd clip>{AUTO_RESOLVE_KIND_LABELS[r.kind || ''] || r.kind || '—'}</DataTd>
                        <DataTd>
                          <Text size="xs" lineClamp={3}>
                            {r.reason || '—'}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {(r.text_snippet || '—').trim() || '—'}
                          </Text>
                        </DataTd>
                      </DataTr>
                    ))}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Tabs.Panel>

          <Tabs.Panel value="queue-blocks" pt="sm">
            <Alert variant="light" color="blue" mb="sm" title="Как разблокировать очередь">
              <Stack gap="xs">
                <Text size="xs">
                  Очередь «встаёт», если метки в БД или Redis не снялись после сбоя воркера, таймаута vLLM или
                  рестарта. Для одного тикета: «Снять и в очередь» — снимает все типы блокировок и ставит{' '}
                  <code>process_single_ticket_with_ai</code> (force). «Только снять» — только очистка; дальше тикет
                  подхватит <code>monitor_ai_tickets</code> (каждую минуту) или новое сообщение клиента.
                </Text>
                <Text size="xs">
                  Не снимайте Lock, если тикет реально обрабатывается (активная задача в Celery / ответ vLLM в
                  процессе) — иначе возможен дубль ответа. По умолчанию из списка скрыты тикеты с Released; снятие
                  Released отключено в «Снимать типы» — включите вручную, если нужно. Отметьте строки галочкой и
                  примените действие к выбранным.
                </Text>
                <Text size="xs" fw={600}>
                  Обозначения в колонке «Блокировки»:
                </Text>
                <Stack gap={4}>
                  {QUEUE_BLOCK_LEGEND.map((item) => (
                    <Text key={item.kind} size="xs">
                      <Badge
                        size="xs"
                        variant="light"
                        color={
                          item.kind.startsWith('redis') || item.kind === 'legacy_fifo'
                            ? 'orange'
                            : item.kind === 'released_from_ai'
                              ? 'gray'
                              : 'red'
                        }
                        mr={6}
                      >
                        {QUEUE_BLOCK_KIND_SHORT[item.kind] || item.kind}
                      </Badge>
                      <strong>{item.title}</strong> — {item.description}
                    </Text>
                  ))}
                </Stack>
              </Stack>
            </Alert>
            <Card withBorder padding="sm" radius="md" mb="sm">
              <Group align="flex-end" wrap="wrap" gap="sm">
                <MultiSelect
                  size="xs"
                  label="Исключить из списка"
                  description="Тикет скрыт, если есть любой из выбранных типов"
                  data={QUEUE_BLOCK_KIND_SELECT_DATA}
                  value={queueBlockExcludeKinds}
                  onChange={setQueueBlockExcludeKinds}
                  maw={320}
                  clearable
                  placeholder="Нет"
                />
                <MultiSelect
                  size="xs"
                  label="Показать только с типами"
                  description="Пусто = все (кроме исключённых)"
                  data={QUEUE_BLOCK_KIND_SELECT_DATA}
                  value={queueBlockIncludeKinds}
                  onChange={setQueueBlockIncludeKinds}
                  maw={320}
                  clearable
                  placeholder="Все типы"
                />
                <MultiSelect
                  size="xs"
                  label="Снимать типы"
                  description="Какие метки снимать при действиях"
                  data={QUEUE_BLOCK_KIND_SELECT_DATA}
                  value={queueBlockClearKinds}
                  onChange={setQueueBlockClearKinds}
                  maw={320}
                  clearable
                  placeholder="Выберите типы"
                />
              </Group>
            </Card>
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Text size="xs" c="dimmed">
                Всего: <strong>{queueBlockTotal}</strong>
                {selectedQueueBlockCount > 0 ? (
                  <>
                    {' '}
                    · выбрано: <strong>{selectedQueueBlockCount}</strong>
                  </>
                ) : null}
              </Text>
              <Group gap="xs" wrap="wrap" align="center">
                {selectedQueueBlockCount > 0 ? (
                  <>
                    <Button
                      size="xs"
                      variant="light"
                      loading={clearingBatchQueueBlocks}
                      disabled={queueBlocksBusy || !canClearQueueBlockKinds}
                      onClick={() => void clearSelectedQueueBlocks(false)}
                    >
                      Снять с выбранных
                    </Button>
                    <Button
                      size="xs"
                      variant="filled"
                      loading={clearingBatchQueueBlocks}
                      disabled={queueBlocksBusy || !canClearQueueBlockKinds}
                      onClick={() => void clearSelectedQueueBlocks(true)}
                    >
                      Снять с выбранных и в очередь
                    </Button>
                  </>
                ) : null}
                <Button
                  size="xs"
                  variant="light"
                  loading={clearingAllQueueBlocks}
                  disabled={queueBlocksBusy || queueBlockTotal === 0 || !canClearQueueBlockKinds}
                  onClick={() => void clearAllQueueBlocks(false)}
                >
                  Снять все блокировки
                </Button>
                <Button
                  size="xs"
                  variant="filled"
                  loading={clearingAllQueueBlocks}
                  disabled={queueBlocksBusy || queueBlockTotal === 0 || !canClearQueueBlockKinds}
                  onClick={() => void clearAllQueueBlocks(true)}
                >
                  Снять все и в очередь
                </Button>
                <NativeSelect
                  size="xs"
                  data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                  value={String(pageSize)}
                  onChange={(e) => onPageSizeChange(e.currentTarget.value)}
                  w={72}
                />
                {pagesQueueBlocks > 1 ? (
                  <PaginationBar
                    page={page}
                    pageCount={pagesQueueBlocks}
                    total={queueBlockTotal}
                    disabled={loading}
                    onPageChange={setPage}
                  />
                ) : null}
              </Group>
            </Group>
            {loading && activeTab === 'queue-blocks' ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}
            {!loading && queueBlockRows.length === 0 ? (
              <EmptyState title="Пусто" description="Нет тикетов с блокировками очереди AI." />
            ) : null}
            {queueBlockRows.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh variantNarrow>
                        <Checkbox
                          size="xs"
                          aria-label="Выбрать все на странице"
                          checked={allQueueBlocksPageSelected}
                          indeterminate={
                            selectedQueueBlockCount > 0 &&
                            !allQueueBlocksPageSelected &&
                            queueBlockRows.some((r) => selectedQueueBlockIds.has(r.ticket_id))
                          }
                          onChange={toggleSelectAllQueueBlocksPage}
                          disabled={queueBlocksBusy}
                        />
                      </DataTh>
                      <DataTh>Обращение</DataTh>
                      <DataTh>Проект</DataTh>
                      <DataTh>Статус</DataTh>
                      <DataTh>Блокировки</DataTh>
                      <DataTh>Обновлено</DataTh>
                      <DataTh>Действия</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {queueBlockRows.map((r) => (
                      <DataTr key={r.ticket_id}>
                        <DataTd>
                          <Checkbox
                            size="xs"
                            aria-label={`Выбрать #${r.ticket_number}`}
                            checked={selectedQueueBlockIds.has(r.ticket_id)}
                            onChange={(e) =>
                              toggleQueueBlockSelection(r.ticket_id, e.currentTarget.checked)
                            }
                            disabled={queueBlocksBusy}
                          />
                        </DataTd>
                        <DataTd>
                          <Group gap={4} wrap="nowrap">
                            <Text
                              component="button"
                              type="button"
                              size="xs"
                              c="blue"
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                              onClick={() =>
                                openTicket({
                                  id: r.ticket_id,
                                  ticket_number: r.ticket_number,
                                  project_slug: r.project_slug,
                                })
                              }
                            >
                              #{r.ticket_number}
                            </Text>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              aria-label="В новой вкладке"
                              onClick={() =>
                                openTicketNewTab({
                                  id: r.ticket_id,
                                  ticket_number: r.ticket_number,
                                  project_slug: r.project_slug,
                                })
                              }
                            >
                              <IconExternalLink size={14} />
                            </ActionIcon>
                          </Group>
                        </DataTd>
                        <DataTd clip>{r.project_name || '—'}</DataTd>
                        <DataTd clip>{r.status}</DataTd>
                        <DataTd>
                          <Group gap={4} wrap="wrap">
                            {(r.blocks ?? []).map((b) => (
                              <Badge
                                key={`${r.ticket_id}-${b.kind}`}
                                size="xs"
                                variant="light"
                                color={
                                  b.kind.startsWith('redis') || b.kind === 'legacy_fifo'
                                    ? 'orange'
                                    : b.kind === 'released_from_ai'
                                      ? 'gray'
                                      : 'red'
                                }
                                title={b.label + (b.value ? `: ${b.value}` : '')}
                              >
                                {QUEUE_BLOCK_KIND_SHORT[b.kind] || b.kind}
                              </Badge>
                            ))}
                          </Group>
                        </DataTd>
                        <DataTd muted>
                          <Text size="xs">{r.updated_at ? formatDateTimeDot(r.updated_at) : '—'}</Text>
                        </DataTd>
                        <DataTd>
                          <Group gap={4} wrap="nowrap">
                            <Button
                              size="compact-xs"
                              variant="filled"
                              loading={clearingTicketId === r.ticket_id}
                              disabled={
                                queueBlocksBusy ||
                                !canClearQueueBlockKinds ||
                                (clearingTicketId != null && clearingTicketId !== r.ticket_id)
                              }
                              onClick={() => void clearQueueBlocks(r.ticket_id, true)}
                            >
                              Снять и в очередь
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="light"
                              loading={clearingTicketId === r.ticket_id}
                              disabled={
                                queueBlocksBusy ||
                                !canClearQueueBlockKinds ||
                                (clearingTicketId != null && clearingTicketId !== r.ticket_id)
                              }
                              onClick={() => void clearQueueBlocks(r.ticket_id, false)}
                            >
                              Только снять
                            </Button>
                          </Group>
                        </DataTd>
                      </DataTr>
                    ))}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Tabs.Panel>

          <Tabs.Panel value="mozgobot-report" pt="sm">
            <DebugMozgobotReportTab projects={projects} defaultHours={hours} />
          </Tabs.Panel>

          <Tabs.Panel value="slow-first-response" pt="sm">
            <DebugSlowFirstResponseTab projects={projects} users={users} openTicket={openTicket} />
          </Tabs.Panel>

          <Tabs.Panel value="pending-history" pt="sm">
            <DebugPendingHistoryTab projects={projects} users={users} openTicket={openTicket} />
          </Tabs.Panel>

          <Tabs.Panel value="failed-deliveries" pt="sm">
            <FailedDeliveriesPanel />
          </Tabs.Panel>

          <Tabs.Panel value="telegram-backfill" pt="sm">
            <Stack gap="sm">
              <Alert variant="light" color="yellow" title="Ограничение Telegram Bot API">
                <Text size="xs">
                  Эта операция получает только pending updates, которые Telegram ещё не подтвердил через offset. Если
                  бот уже подтвердил сообщения через offset, Telegram API повторно их не отдаст.
                </Text>
              </Alert>
              <Card withBorder padding="sm" radius="md">
                <Group align="flex-end" wrap="wrap" gap="sm">
                  <NativeSelect
                    size="xs"
                    label="Telegram-канал"
                    data={telegramChannelSelectData.length ? telegramChannelSelectData : [{ value: '', label: 'Нет Telegram-каналов' }]}
                    value={telegramBackfillChannelId}
                    onChange={(e) => setTelegramBackfillChannelId(e.currentTarget.value)}
                    maw={320}
                    disabled={telegramBackfillLoading || telegramChannelSelectData.length === 0}
                  />
                  <NumberInput
                    size="xs"
                    label="Limit"
                    description="1–100 за batch"
                    value={telegramBackfillLimit}
                    onChange={(v) => setTelegramBackfillLimit(typeof v === 'number' ? v : 100)}
                    min={1}
                    max={100}
                    w={110}
                    disabled={telegramBackfillLoading}
                  />
                  <NumberInput
                    size="xs"
                    label="Batches"
                    description="1–50"
                    value={telegramBackfillMaxBatches}
                    onChange={(v) => setTelegramBackfillMaxBatches(typeof v === 'number' ? v : 10)}
                    min={1}
                    max={50}
                    w={120}
                    disabled={telegramBackfillLoading}
                  />
                  <Checkbox
                    size="xs"
                    label="Только сегодня"
                    checked={telegramBackfillTodayOnly}
                    onChange={(e) => setTelegramBackfillTodayOnly(e.currentTarget.checked)}
                    disabled={telegramBackfillLoading}
                  />
                  <Button
                    size="xs"
                    variant="light"
                    loading={telegramBackfillLoading}
                    disabled={!telegramBackfillChannelId}
                    onClick={() => void runTelegramBackfill(false)}
                  >
                    Проверить pending
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    loading={telegramBackfillLoading}
                    disabled={!telegramBackfillChannelId}
                    onClick={() => void runTelegramBackfill(true)}
                  >
                    Догрузить в сервисдеск
                  </Button>
                </Group>
              </Card>

              {telegramBackfillResult ? (
                <Card withBorder padding="sm" radius="md">
                  <Stack gap="xs">
                    <Group gap="md" wrap="wrap">
                      <Text size="xs">
                        Режим: <strong>{telegramBackfillResult.applied ? 'догрузка' : 'проверка'}</strong>
                      </Text>
                      <Text size="xs">
                        Batches: <strong>{telegramBackfillResult.batches}</strong>
                      </Text>
                      <Text size="xs">
                        Найдено updates: <strong>{telegramBackfillResult.updates_seen}</strong>
                      </Text>
                      <Text size="xs">
                        Загружено: <strong>{telegramBackfillResult.updates_processed}</strong>
                      </Text>
                      <Text size="xs">
                        Пропущено старых: <strong>{telegramBackfillResult.updates_skipped_old}</strong>
                      </Text>
                      <Text size="xs">
                        Без сообщения: <strong>{telegramBackfillResult.updates_without_message}</strong>
                      </Text>
                      <Text size="xs">
                        Last update id: <strong>{telegramBackfillResult.last_update_id ?? '—'}</strong>
                      </Text>
                    </Group>
                    {telegramBackfillResult.note ? (
                      <Text size="xs" c="dimmed">
                        {telegramBackfillResult.note}
                      </Text>
                    ) : null}
                    {telegramBackfillResult.errors?.length ? (
                      <Alert color="red" variant="light" title="Ошибки обработки">
                        <Stack gap={4}>
                          {telegramBackfillResult.errors.slice(0, 20).map((err, idx) => (
                            <Text key={`${err.update_id || 'x'}-${idx}`} size="xs">
                              {err.update_id ? `update ${err.update_id}: ` : ''}
                              {err.error}
                            </Text>
                          ))}
                        </Stack>
                      </Alert>
                    ) : null}
                  </Stack>
                </Card>
              ) : null}

              <Card withBorder padding="sm" radius="md">
                <Stack gap="xs">
                  <Group justify="space-between" align="center" wrap="wrap">
                    <Text size="sm" fw={600}>
                      Уже загружено из Telegram
                    </Text>
                    <Group gap="xs" wrap="wrap">
                      <TextInput
                        size="xs"
                        placeholder="Номер, текст или клиент"
                        value={telegramIngestedSearch}
                        onChange={(e) => setTelegramIngestedSearch(e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void loadTelegramIngestedTickets(1)
                        }}
                        disabled={telegramIngestedBusy}
                      />
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconRefresh size={14} />}
                        loading={telegramIngestedBusy}
                        disabled={!telegramBackfillChannelId}
                        onClick={() => void loadTelegramIngestedTickets(1)}
                      >
                        Показать
                      </Button>
                    </Group>
                  </Group>

                  <Text size="xs" c="dimmed">
                    Показывает обращения и сообщения, которые уже попали в базу из выбранного Telegram-канала. Это помогает найти апдейты, которые Telegram уже не отдаёт повторно.
                  </Text>

                  {telegramIngestedList ? (
                    <Stack gap="xs">
                      <Group justify="space-between" wrap="wrap">
                        <Text size="xs" c="dimmed">
                          Найдено обращений: <strong>{telegramIngestedList.total}</strong>
                        </Text>
                        {telegramIngestedList.pages > 1 ? (
                          <PaginationBar
                            page={telegramIngestedPage}
                            pageCount={telegramIngestedList.pages}
                            total={telegramIngestedList.total}
                            disabled={telegramIngestedBusy}
                            onPageChange={(nextPage) => void loadTelegramIngestedTickets(nextPage)}
                          />
                        ) : null}
                      </Group>

                      {telegramIngestedBusy ? (
                        <PageCenterLoader label="Загрузка" minHeight={120} />
                      ) : telegramIngestedList.items.length ? (
                        <DataTableScroll>
                          <DataTable>
                            <DataThead>
                              <DataTr>
                                <DataTh>Обращение</DataTh>
                                <DataTh>Статус</DataTh>
                                <DataTh>Клиент</DataTh>
                                <DataTh>Сообщения</DataTh>
                                <DataTh>Последнее сообщение</DataTh>
                                <DataTh>Создано</DataTh>
                                <DataTh>Действие</DataTh>
                              </DataTr>
                            </DataThead>
                            <DataTbody>
                              {telegramIngestedList.items.map((item) => (
                                <DataTr key={item.id}>
                                  <DataTd>
                                    <Text size="xs" fw={600}>
                                      #{item.ticket_number}
                                    </Text>
                                    <Text size="xs" c="dimmed" lineClamp={2}>
                                      {item.subject || item.id}
                                    </Text>
                                    <Text size="xs" c="dimmed" lineClamp={1}>
                                      {item.project_name || item.project_id}
                                    </Text>
                                  </DataTd>
                                  <DataTd>
                                    <Badge size="xs" variant="light">
                                      {statusLabel(item.status)}
                                    </Badge>
                                  </DataTd>
                                  <DataTd>
                                    <Text size="xs" lineClamp={1}>
                                      {item.customer_name || item.customer_email || item.customer_external_id || '—'}
                                    </Text>
                                  </DataTd>
                                  <DataTd>
                                    <Text size="xs">
                                      {item.actual_messages_count} всего
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {item.customer_messages_count} клиент / {item.operator_messages_count} оператор
                                    </Text>
                                  </DataTd>
                                  <DataTd>
                                    <Text size="xs" lineClamp={2}>
                                      {item.last_message_content || '—'}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {item.last_message_at ? formatDateTimeDot(item.last_message_at) : '—'}
                                    </Text>
                                  </DataTd>
                                  <DataTd muted>
                                    <Text size="xs">{item.created_at ? formatDateTimeDot(item.created_at) : '—'}</Text>
                                  </DataTd>
                                  <DataTd>
                                    <ActionIcon
                                      size="sm"
                                      variant="light"
                                      title="Открыть обращение"
                                      onClick={() => {
                                        openOrSelectTab(`/tickets/${encodeURIComponent(ticketPathSegmentFromListItem(item))}`)
                                      }}
                                    >
                                      <IconExternalLink size={14} />
                                    </ActionIcon>
                                  </DataTd>
                                </DataTr>
                              ))}
                            </DataTbody>
                          </DataTable>
                        </DataTableScroll>
                      ) : (
                        <EmptyState title="Пусто" description="По выбранному Telegram-каналу обращений в базе не найдено." />
                      )}
                    </Stack>
                  ) : null}
                </Stack>
              </Card>

              <Card withBorder padding="sm" radius="md">
                <Stack gap="xs">
                  <Group justify="space-between" align="center" wrap="wrap">
                    <Text size="sm" fw={600}>
                      Workspace у обращений
                    </Text>
                    <Group gap="xs" wrap="wrap">
                      <Button
                        size="xs"
                        variant="default"
                        loading={ticketWorkspaceRepairBusy}
                        disabled={!telegramBackfillChannelId}
                        onClick={() => void runTicketWorkspaceRepair(false)}
                      >
                        Проверить
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        loading={ticketWorkspaceRepairListBusy}
                        disabled={!telegramBackfillChannelId}
                        onClick={() => void loadTicketWorkspaceRepairList(1)}
                      >
                        Показать список
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="orange"
                        loading={ticketWorkspaceRepairBusy}
                        disabled={!telegramBackfillChannelId}
                        onClick={() => void runTicketWorkspaceRepair(true)}
                      >
                        Исправить все
                      </Button>
                    </Group>
                  </Group>

                  {ticketWorkspaceRepairResult ? (
                    <Text size="xs" c="dimmed">
                      К исправлению: {ticketWorkspaceRepairResult.tickets_to_fix}. Исправлено: {ticketWorkspaceRepairResult.tickets_updated}. Клиенты: {ticketWorkspaceRepairResult.customers_updated}. Перенумеровано: {ticketWorkspaceRepairResult.tickets_renumbered_due_to_conflict}.
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      Проверяет обращения workspace-а выбранного Telegram-канала и проставляет им workspace из проекта.
                    </Text>
                  )}

                  {ticketWorkspaceRepairList ? (
                    <Stack gap="xs">
                      <Group justify="space-between" wrap="wrap">
                        <Text size="xs" c="dimmed">
                          Найдено обращений: <strong>{ticketWorkspaceRepairList.total}</strong>
                        </Text>
                        {ticketWorkspaceRepairList.pages > 1 ? (
                          <PaginationBar
                            page={ticketWorkspaceRepairPage}
                            pageCount={ticketWorkspaceRepairList.pages}
                            total={ticketWorkspaceRepairList.total}
                            disabled={ticketWorkspaceRepairListBusy}
                            onPageChange={(nextPage) => void loadTicketWorkspaceRepairList(nextPage)}
                          />
                        ) : null}
                      </Group>

                      {ticketWorkspaceRepairListBusy ? (
                        <PageCenterLoader label="Загрузка" minHeight={120} />
                      ) : ticketWorkspaceRepairList.items.length ? (
                        <DataTableScroll>
                          <DataTable>
                            <DataThead>
                              <DataTr>
                                <DataTh>Обращение</DataTh>
                                <DataTh>Проект</DataTh>
                                <DataTh>Канал</DataTh>
                                <DataTh>Было</DataTh>
                                <DataTh>Будет</DataTh>
                                <DataTh>Создано</DataTh>
                                <DataTh>Действие</DataTh>
                              </DataTr>
                            </DataThead>
                            <DataTbody>
                              {ticketWorkspaceRepairList.items.map((item) => (
                                <DataTr key={item.id}>
                                  <DataTd>
                                    <Text size="xs" fw={600}>
                                      #{item.ticket_number}
                                    </Text>
                                    <Text size="xs" c="dimmed" lineClamp={2}>
                                      {item.subject || item.id}
                                    </Text>
                                  </DataTd>
                                  <DataTd clip>{item.project_name || item.project_id || '—'}</DataTd>
                                  <DataTd clip>{item.channel_name || item.channel_type || '—'}</DataTd>
                                  <DataTd>
                                    <Text size="xs" ff="monospace" lineClamp={1}>
                                      {item.old_workspace_id || 'NULL'}
                                    </Text>
                                  </DataTd>
                                  <DataTd>
                                    <Text size="xs" ff="monospace" lineClamp={1}>
                                      {item.new_workspace_id}
                                    </Text>
                                  </DataTd>
                                  <DataTd muted>
                                    <Text size="xs">{item.created_at ? formatDateTimeDot(item.created_at) : '—'}</Text>
                                  </DataTd>
                                  <DataTd>
                                    <Button
                                      size="compact-xs"
                                      variant="light"
                                      loading={ticketWorkspaceRepairSingleBusy === item.id}
                                      onClick={() => void repairOneTicketWorkspace(item.id)}
                                    >
                                      Исправить
                                    </Button>
                                  </DataTd>
                                </DataTr>
                              ))}
                            </DataTbody>
                          </DataTable>
                        </DataTableScroll>
                      ) : (
                        <EmptyState title="Пусто" description="Нет обращений с неправильным workspace." />
                      )}
                    </Stack>
                  ) : null}
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="email-backfill" pt="sm">
            <DebugEmailBackfillTab />
          </Tabs.Panel>

          <Tabs.Panel value="tickets-export" pt="sm">
            <DebugTicketsExportTab />
          </Tabs.Panel>

          <Tabs.Panel value="db" pt="sm">
            {dbSummary ? (
              <Group gap="md" wrap="wrap" mb="md">
                {Object.entries(dbSummary).map(([k, v]) => (
                  <Text key={k} size="xs">
                    {k}: <strong>{v}</strong>
                  </Text>
                ))}
              </Group>
            ) : null}
            {dbByApp.length > 0 ? (
              <Box mb="lg">
                <Text size="sm" fw={600} mb={6}>
                  По application_name
                </Text>
                <DataTableScroll>
                  <DataTable>
                    <DataThead>
                      <DataTr>
                        <DataTh>application_name</DataTh>
                        <DataTh variantNarrow>Всего</DataTh>
                        <DataTh variantNarrow>Активные</DataTh>
                        <DataTh variantNarrow>Idle</DataTh>
                        <DataTh variantNarrow>Idle tx</DataTh>
                      </DataTr>
                    </DataThead>
                    <DataTbody>
                      {dbByApp.map((r) => (
                        <DataTr key={r.application_name}>
                          <DataTd clip>{r.application_name}</DataTd>
                          <DataTd style={{ textAlign: 'right' }}>{r.total}</DataTd>
                          <DataTd style={{ textAlign: 'right' }}>{r.active}</DataTd>
                          <DataTd style={{ textAlign: 'right' }}>{r.idle}</DataTd>
                          <DataTd style={{ textAlign: 'right' }}>{r.idle_in_transaction}</DataTd>
                        </DataTr>
                      ))}
                    </DataTbody>
                  </DataTable>
                </DataTableScroll>
              </Box>
            ) : null}
            {dbConn.length > 0 ? (
              <Box>
                <Text size="sm" fw={600} mb={6}>
                  Соединения (до 200)
                </Text>
                <DataTableScroll>
                  <DataTable>
                    <DataThead>
                      <DataTr>
                        <DataTh variantNarrow>PID</DataTh>
                        <DataTh>user</DataTh>
                        <DataTh>app</DataTh>
                        <DataTh>state</DataTh>
                        <DataTh>backend</DataTh>
                        <DataTh>wait</DataTh>
                        <DataTh>Δt</DataTh>
                        <DataTh>query</DataTh>
                      </DataTr>
                    </DataThead>
                    <DataTbody>
                      {dbConn.map((c, i) => {
                        const st = (c.state || '').toLowerCase()
                        const qd = c.query_duration_seconds ?? 0
                        const hi = st === 'active' && qd >= 60
                        return (
                          <DataTr key={`${c.pid}-${i}`} style={{ background: hi ? 'rgba(200, 40, 30, 0.1)' : undefined }}>
                            <DataTd>{c.pid}</DataTd>
                            <DataTd clip>{c.usename || '—'}</DataTd>
                            <DataTd clip>{c.application_name || '—'}</DataTd>
                            <DataTd clip>{c.state || '—'}</DataTd>
                            <DataTd clip>{c.backend_type || '—'}</DataTd>
                            <DataTd clip>{c.wait_event || '—'}</DataTd>
                            <DataTd>{qd ? `${qd.toFixed(1)} с` : '—'}</DataTd>
                            <DataTd>
                              <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {c.query || '—'}
                              </Text>
                            </DataTd>
                          </DataTr>
                        )
                      })}
                    </DataTbody>
                  </DataTable>
                </DataTableScroll>
              </Box>
            ) : null}
            {!loading && activeTab === 'db' && !dbConn.length && !dbByApp.length ? (
              <EmptyState title="Нет данных" description="Не удалось получить pg_stat_activity или пусто." />
            ) : null}
          </Tabs.Panel>

          {isOwnerOrAdmin && canDebug ? (
            <Tabs.Panel value="loadtest" pt="sm">
              <SettingsLoadTestTab />
            </Tabs.Panel>
          ) : null}

          {isOwnerOrAdmin && canDebug ? (
            <Tabs.Panel value="shift-breaks" pt="sm">
              <DebugShiftBreaksTab />
            </Tabs.Panel>
          ) : null}

          <Tabs.Panel value="ai-audit" pt="sm">
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Text size="xs" c="dimmed">
                Всего: <strong>{auditTotal}</strong>
              </Text>
              {pagesAudit > 1 ? (
                <PaginationBar
                  page={auditPage}
                  pageCount={pagesAudit}
                  total={auditTotal}
                  disabled={loading}
                  onPageChange={setAuditPage}
                />
              ) : null}
            </Group>
            {loading && activeTab === 'ai-audit' ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}
            {!loading && auditRows.length === 0 ? <EmptyState title="Пусто" description="Записей нет." /> : null}
            {auditRows.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>Время</DataTh>
                      <DataTh>Кто</DataTh>
                      <DataTh>Тип</DataTh>
                      <DataTh>Сущность</DataTh>
                      <DataTh>Проект</DataTh>
                      <DataTh>Описание</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {auditRows.map((item) => (
                      <DataTr key={item.id}>
                        <DataTd muted>
                          <Text size="xs">{formatDateTimeDot(item.created_at)}</Text>
                        </DataTd>
                        <DataTd clip>{item.user_name || item.user_email || 'Система'}</DataTd>
                        <DataTd clip>
                          <Text size="xs" ff="monospace">
                            {item.activity_type || '—'}
                          </Text>
                        </DataTd>
                        <DataTd clip>{item.entity_type || '—'}</DataTd>
                        <DataTd clip>{item.project_name || '—'}</DataTd>
                        <DataTd>
                          <Text size="xs">{item.description || '—'}</Text>
                          {item.details != null ? (
                            <Text size="xs" c="dimmed" mt={4} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(item.details)}
                            </Text>
                          ) : null}
                        </DataTd>
                      </DataTr>
                    ))}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </PageShell>
  )
}
